import { Dropdown, Tooltip, icons } from '@lifebit-ai/lds'
import React, { useCallback, useMemo } from 'react'

import {
  AddLabelButton,
  CloseIcon,
  DisabledAddLabelButtonWrapper,
  LabelText,
  LabelsContainer,
  StyledLabelTag
} from 'modules/resultsV2/Results.styles'
import { ResultLabel } from 'services/results/types'

const MAX_RESULT_LABELS = 5

const normalizeLabelName = (name: string): string => name.trim().toLowerCase()

type DropdownProps = React.ComponentProps<typeof Dropdown>

interface ResultLabelsProps {
  addedLabels?: ResultLabel[]
  allLabels?: ResultLabel[]
  onAddResultLabel: NonNullable<DropdownProps['onAdd']>
  onValidateResultLabel: NonNullable<DropdownProps['onAddValidation']>
  onClickLabelMenuItem: (labelId: string) => void
  onDeleteResultLabel: (labelId: string) => void
}

const ResultLabels: React.FC<ResultLabelsProps> = ({
  addedLabels, // labels already added to the export request
  allLabels, // all labels available for the export request
  onAddResultLabel,
  onValidateResultLabel,
  onClickLabelMenuItem,
  onDeleteResultLabel
}) => {
  const hasReachedMaxLabels = (addedLabels?.length ?? 0) >= MAX_RESULT_LABELS

  const addedLabelIds = useMemo(() => new Set((addedLabels ?? []).map(label => label._id)), [addedLabels])

  const addedLabelNames = useMemo(
    () => new Set((addedLabels ?? []).map(label => normalizeLabelName(label.name))),
    [addedLabels]
  )

  const allLabelNames = useMemo(
    () => new Set((allLabels ?? []).map(label => normalizeLabelName(label.name))),
    [allLabels]
  )

  const labelMenuItems = useMemo(() => {
    if (!allLabels?.length) return []

    return allLabels
      .filter(label => !addedLabelIds.has(label._id) && !addedLabelNames.has(normalizeLabelName(label.name)))
      .map(label => ({
        key: label._id,
        label: label.name
      }))
  }, [addedLabelIds, addedLabelNames, allLabels])

  const handleValidateResultLabel = useCallback<NonNullable<DropdownProps['onAddValidation']>>(
    value => {
      const validationResult = onValidateResultLabel(value)

      if (validationResult?.isValid === false) {
        return validationResult
      }

      const normalizedValue = normalizeLabelName(value)

      if (addedLabelNames.has(normalizedValue)) {
        return {
          isValid: false,
          message: 'This label has already been added'
        }
      }

      // Reject creating a new label whose name already exists in the catalog
      // (case-insensitive); the user should pick the existing label instead.
      if (allLabelNames.has(normalizedValue)) {
        return {
          isValid: false,
          message: 'A label with this name already exists'
        }
      }

      // Preserve the parent's result (including any message) when it passed;
      // only synthesize a valid result when the parent returned nothing.
      return validationResult ?? { isValid: true }
    },
    [addedLabelNames, allLabelNames, onValidateResultLabel]
  )

  return (
    <LabelsContainer>
      {addedLabels?.map(label => (
        <StyledLabelTag key={label._id}>
          <span>{label.name}</span>
          <CloseIcon onClick={() => onDeleteResultLabel(label._id)} />
        </StyledLabelTag>
      ))}
      {hasReachedMaxLabels ? (
        <Tooltip title={`Maximum of ${MAX_RESULT_LABELS} labels reached`}>
          <DisabledAddLabelButtonWrapper>
            <AddLabelButton disabled={true}>
              <icons.PlusSmall />
              <LabelText>Add Label</LabelText>
            </AddLabelButton>
          </DisabledAddLabelButtonWrapper>
        </Tooltip>
      ) : (
        <Dropdown
          showSearch
          onAdd={onAddResultLabel}
          compact={true}
          header={true}
          menu={{
            items: labelMenuItems,
            onClick: ({ key }) => {
              onClickLabelMenuItem(String(key))
            }
          }}
          onAddValidation={handleValidateResultLabel}
        >
          <AddLabelButton>
            <icons.PlusSmall />
            <LabelText>Add Label</LabelText>
          </AddLabelButton>
        </Dropdown>
      )}
    </LabelsContainer>
  )
}

export default ResultLabels
